import React, { useEffect, useState, useMemo } from "react";
import { useAppContext } from "@/context/AppContext";
import { useNavigation } from "@/context/NavigationContext";
import { Button } from "@/components/ui/button";
import {
  Plane,
  Tag,
  Key,
  Settings,
  Plus,
  Activity,
  Compass,
  MapPin,
  Sparkles,
  AlertTriangle,
  Eye,
  Power,
  PowerOff,
  EyeOff,
  ShoppingBag,
  Trash2,
  FileText
} from "lucide-react";
import InstrumentLoader from "@/components/InstrumentLoader";

const SellerDashboard = () => {
  const {
    myAircraftListings,
    myAircraftRentals,
    myAircraftParts,
    myExperiences,
    fetchMyAircraftListings,
    fetchMyAircraftRentals,
    fetchMyAircraftParts,
    fetchMyExperiences,
    isMyAircraftListingsLoading,
    isMyAircraftRentalsLoading,
    isMyAircraftPartsLoading,
    isMyExperiencesLoading,
    toggleAircraftActiveState,
    toggleRentalActiveState,
    togglePartActiveState,
    toggleExperienceActiveState,
  } = useAppContext();

  const { navigateTo } = useNavigation();
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      fetchMyAircraftListings(),
      fetchMyAircraftRentals(),
      fetchMyAircraftParts(),
      fetchMyExperiences()
    ]).catch((err) => {
      console.error("Error loading seller dashboard data:", err);
    });
  }, [fetchMyAircraftListings, fetchMyAircraftRentals, fetchMyAircraftParts, fetchMyExperiences]);

  const isInitialLoading =
    (myAircraftListings === null && isMyAircraftListingsLoading) ||
    (myAircraftRentals === null && isMyAircraftRentalsLoading) ||
    (myAircraftParts === null && isMyAircraftPartsLoading) ||
    (myExperiences === null && isMyExperiencesLoading) ||
    actionLoading;

  const handleToggleActive = async (type: string, item: any) => {
    setActionLoading(true);
    try {
      if (type === "EXPERIENCE") {
        await toggleExperienceActiveState(item.experienceId);
      } else if (type === "SALE") {
        await toggleAircraftActiveState(item.listingId);
      } else if (type === "RENTAL") {
        await toggleRentalActiveState(item.rentalId);
      } else if (type === "PART") {
        await togglePartActiveState(item.partId);
      }
    } catch (error) {
      console.error("Eroare la comutarea statusului activ:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteItem = async (type: string, item: any) => {
    console.log(`[ACARS_OPS] Delete requested for ${type} unit ID:`, item.listingId || item.rentalId || item.partId || item.experienceId);
  };

  const formatPrice = (price: number) =>
    new Intl.NumberFormat("ro-RO", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(price);

  const listings = myAircraftListings || [];
  const rentals = myAircraftRentals || [];
  const parts = myAircraftParts || [];
  const experiences = myExperiences || [];

  return (
    <div className="min-h-screen bg-[#030712] text-slate-300 pb-24 font-sans antialiased selection:bg-blue-600/30 selection:text-white">
      <InstrumentLoader visible={isInitialLoading} />

      <div className="container py-12 max-w-5xl px-4 sm:px-6">

        <header className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-16 bg-[#0B0F19]/60 border border-slate-800/40 p-8 rounded-3xl backdrop-blur-xl">
          <div className="absolute top-0 left-0 w-64 h-64 bg-blue-600/5 blur-3xl -z-10 animate-pulse" />
          <div className="space-y-1">
            <p className="text-blue-500 font-mono text-[10px] uppercase tracking-[0.35em] font-black flex items-center gap-2">
              <Activity size={12} className="animate-pulse" /> CONTROL_PANEL // INVENTORY
            </p>
            <h1 className="text-4xl font-black text-white tracking-tight uppercase">
              Seller <span className="text-slate-500 font-light font-sans lowercase italic">ops</span>
            </h1>
          </div>
          <div>
            <Button
              onClick={() => navigateTo("list-category")}
              className="bg-blue-600 hover:bg-blue-500 text-white font-black rounded-xl px-6 h-12 text-xs uppercase tracking-widest shadow-lg shadow-blue-900/30 active:scale-[0.98] transition-all flex gap-2 items-center"
            >
              <Plus size={15} className="stroke-[3]" /> Adaugă Listare
            </Button>
          </div>
        </header>

        <div className="space-y-16">

          {/* SECTIUNE: VANZARI (LISTINGS) */}
          <section className="space-y-6">
            <div className="flex items-center justify-between border-b border-slate-900 pb-4">
              <div className="flex items-center gap-3">
                <Tag size={16} className="text-orange-500" />
                <h2 className="text-xs font-black uppercase tracking-[0.25em] text-white">Aeronave la Vânzare</h2>
              </div>
              <span className="font-mono text-[10px] text-slate-500 font-bold bg-slate-900 px-2.5 py-1 rounded-md border border-slate-800">{listings.length} unități</span>
            </div>

            {listings.length === 0 ? (
              <EmptyState message="Nu ai nicio aeronavă listată pentru vânzare." />
            ) : (
              <div className="grid gap-5">
                {listings.map((item) => (
                  <AssetCard
                    key={item.listingId}
                    item={item}
                    type="SALE"
                    onAction={() => navigateTo("aircraft-details", { listingId: item.listingId, aircraftListing: item, seller: 1 })}
                    onToggleActive={() => handleToggleActive("SALE", item)}
                    onDelete={() => handleDeleteItem("SALE", item)}
                    formatPrice={formatPrice}
                  />
                ))}
              </div>
            )}
          </section>

          {/* SECTIUNE: INCHIRIERI (RENTALS) */}
          <section className="space-y-6">
            <div className="flex items-center justify-between border-b border-slate-900 pb-4">
              <div className="flex items-center gap-3">
                <Key size={16} className="text-blue-500" />
                <h2 className="text-xs font-black uppercase tracking-[0.25em] text-white">Flotă de Închiriere</h2>
              </div>
              <span className="font-mono text-[10px] text-slate-500 font-bold bg-slate-900 px-2.5 py-1 rounded-md border border-slate-800">{rentals.length} unități</span>
            </div>

            {rentals.length === 0 ? (
              <EmptyState message="Nu ai nicio aeronavă configurată pentru închiriere." />
            ) : (
              <div className="grid gap-5">
                {rentals.map((item) => (
                  <AssetCard
                    key={item.rentalId}
                    item={item}
                    type="RENTAL"
                    onAction={() => navigateTo("rental-details", { rentalId: item.rentalId, aircraftRental: item, seller: 1 })}
                    onToggleActive={() => handleToggleActive("RENTAL", item)}
                    onDelete={() => handleDeleteItem("RENTAL", item)}
                    formatPrice={formatPrice}
                  />
                ))}
              </div>
            )}
          </section>

          {/* SECTIUNE: PIESE & COMPONENTE (PARTS) */}
          <section className="space-y-6">
            <div className="flex items-center justify-between border-b border-slate-900 pb-4">
              <div className="flex items-center gap-3">
                <Settings size={16} className="text-emerald-500" />
                <h2 className="text-xs font-black uppercase tracking-[0.25em] text-white">Piese & Componente</h2>
              </div>
              <span className="font-mono text-[10px] text-slate-500 font-bold bg-slate-900 px-2.5 py-1 rounded-md border border-slate-800">{parts.length} unități</span>
            </div>

            {parts.length === 0 ? (
              <EmptyState message="Nu ai nicio piesă înregistrată în inventar." />
            ) : (
              <div className="grid gap-5">
                {parts.map((item) => (
                  <AssetCard
                    key={item.partId}
                    item={item}
                    type="PART"
                    onAction={() => navigateTo("part-details", { partId: item.partId, part: item, seller: 1 })}
                    onToggleActive={() => handleToggleActive("PART", item)}
                    onDelete={() => handleDeleteItem("PART", item)}
                    formatPrice={formatPrice}
                  />
                ))}
              </div>
            )}
          </section>

          {/* SECTIUNE: EXPERIENȚE AVIATICE (EXPERIENCES) */}
          <section className="space-y-6">
            <div className="flex items-center justify-between border-b border-slate-900 pb-4">
              <div className="flex items-center gap-3">
                <Compass size={16} className="text-red-500" />
                <h2 className="text-xs font-black uppercase tracking-[0.25em] text-white">Experiențe Aviatice</h2>
              </div>
              <span className="font-mono text-[10px] text-slate-500 font-bold bg-slate-900 px-2.5 py-1 rounded-md border border-slate-800">{experiences.length} unități</span>
            </div>

            {experiences.length === 0 ? (
              <EmptyState message="Nu ai nicio experiență aviatică publicată." />
            ) : (
              <div className="grid gap-5">
                {experiences.map((item) => (
                  <AssetCard
                    key={item.experienceId}
                    item={item}
                    type="EXPERIENCE"
                    onAction={() => navigateTo("experience-details", { experienceId: item.experienceId, experience: item, seller: 1 })}
                    onToggleActive={() => handleToggleActive("EXPERIENCE", item)}
                    onDelete={() => handleDeleteItem("EXPERIENCE", item)}
                    formatPrice={formatPrice}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

const AssetCard = ({ item, type, onAction, onToggleActive, onDelete, formatPrice }: any) => {
  const { navigateTo } = useNavigation();
  const isRental = type === "RENTAL";
  const isPart = type === "PART";
  const isSale = type === "SALE";
  const isExperience = type === "EXPERIENCE";

  const isDraft = item.draft === true;
  const isUnverified = !isDraft && item.verified !== true;
  const isSoldOut = isPart && item.quantity === 0;
  const isArchived = !item.active && !isDraft && !isUnverified && !isSoldOut;
  const isActiveVerified = item.active && item.verified && !isDraft && !isSoldOut;

  const cardTheme = useMemo(() => {
    if (isDraft) {
      return {
        border: "border-red-600/60 hover:border-red-500",
        badgeBg: "bg-red-600",
        glow: "shadow-[0_0_20px_rgba(220,38,38,0.12)]",
        statusText: "text-red-400",
        statusLabel: "STATUS: DRAFT",
        badgeLabel: "DRAFT"
      };
    }
    if (isUnverified) {
      return {
        border: "border-amber-500/60 hover:border-amber-400",
        badgeBg: "bg-amber-500 text-slate-950 font-black",
        glow: "shadow-[0_0_20px_rgba(245,158,11,0.12)]",
        statusText: "text-amber-400",
        statusLabel: "STATUS: SPRE VERIFICARE",
        badgeLabel: "PENDING"
      };
    }
    if (isActiveVerified) {
      return {
        border: "border-emerald-500/50 hover:border-emerald-400",
        badgeBg: "bg-emerald-600",
        glow: "shadow-[0_0_20px_rgba(16,185,129,0.12)]",
        statusText: "text-emerald-400",
        statusLabel: "STATUS: ACTIV",
        badgeLabel: "ACTIVE"
      };
    }
    if (isSoldOut) {
      return {
        border: "border-slate-800/80",
        badgeBg: "bg-slate-700",
        glow: "",
        statusText: "text-slate-500",
        statusLabel: "STATUS: STOC EPUIZAT",
        badgeLabel: "SOLD"
      };
    }
    return {
      border: "border-slate-800/70 opacity-60 grayscale-[0.3]",
      badgeBg: "bg-slate-800",
      glow: "",
      statusText: "text-slate-500",
      statusLabel: "STATUS: ARHIVAT",
      badgeLabel: "ARCHIVED"
    };
  }, [isDraft, isUnverified, isActiveVerified, isSoldOut]);

  const firstImage = useMemo(() => {
    if (item.mediaUrls && item.mediaUrls.length > 0) {
      return item.mediaUrls.find((url: string) => url && url.trim() !== "") || null;
    }
    if (item.media && item.media.length > 0) {
      const data = item.media[0].mediaData;
      if (!data) return null;
      return data.startsWith("data:") ? data : `data:image/jpeg;base64,${data}`;
    }
    if (item.mediaFiles && item.mediaFiles.length > 0) return item.mediaFiles[0];
    return null;
  }, [item.mediaUrls, item.media, item.mediaFiles]);

  const grossPrice = item.grossValue ?? 0;

  const handleSettingsClick = () => {
    if (isSale) {
      navigateTo("list-aircraft", { aircraftListing: item });
    } else if (isRental) {
      navigateTo("rent-aircraft", { aircraftRental: item });
    } else if (isPart) {
      navigateTo("list-parts", { aircraftPart: item });
    } else if (isExperience) {
      navigateTo("list-experience", { experience: item });
    }
  };

  const primaryLocation = item.location || item.baseAirport || null;

  return (
    <div className={`bg-[#0B0F19]/60 border rounded-3xl p-6 flex flex-col gap-6 transition-all duration-300 ${cardTheme.border} ${cardTheme.glow}`}>

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 flex-1 min-w-0 w-full">

          <div className="h-20 w-full sm:w-28 shrink-0 rounded-2xl bg-slate-950 border border-slate-800/80 flex items-center justify-center relative overflow-hidden group/thumb shadow-inner">
            {firstImage ? (
              <img
                src={firstImage}
                className="w-full h-full object-cover group-hover/thumb:scale-105 transition-transform duration-500"
                alt="Asset"
              />
            ) : (
              <div className="opacity-15">
                {isExperience ? <Compass className="h-8 w-8 text-slate-400" /> : <Plane className="h-8 w-8 text-slate-400" />}
              </div>
            )}
            <div className={`absolute top-2 left-2 px-2 py-0.5 rounded text-[8px] font-black text-white tracking-widest shadow-md ${cardTheme.badgeBg}`}>
              {cardTheme.badgeLabel}
            </div>
          </div>

          <div className="flex-1 min-w-0 space-y-1.5">
            <h3 className="text-lg font-black text-white uppercase tracking-tight truncate">
              {item.manufacturer ? `${item.manufacturer} ${item.model || ""}` : item.title || item.registration || "Produs Fără Nume"}
            </h3>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-medium text-slate-500 uppercase tracking-wider">
              {primaryLocation && (
                <span className="flex items-center gap-1.5 text-slate-400">
                  <MapPin size={12} className="text-blue-500" /> {primaryLocation}
                </span>
              )}

              <span className="flex items-center gap-1.5 font-mono text-[10px]">
                <Sparkles size={12} className={cardTheme.statusText} />
                <span className={`font-bold ${cardTheme.statusText}`}>
                  {cardTheme.statusLabel}
                </span>
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row lg:items-center justify-between gap-5 w-full lg:w-auto shrink-0 border-t lg:border-t-0 border-slate-900/60 pt-4 lg:pt-0">

          <div className="flex items-center justify-between sm:justify-end gap-3 bg-slate-950/50 border border-slate-900 rounded-2xl px-5 py-3.5 min-w-[160px]">
            <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">Preț:</span>
            <span className="text-base font-black text-white font-mono tracking-tight">
              {grossPrice > 0 ? formatPrice(grossPrice) : "LA CERERE"}{isRental && "/h"}
            </span>
          </div>

          <div className="flex gap-2.5 items-center justify-end shrink-0">
            {!isDraft && !isUnverified && (
              <IconButtonWithAcarsTooltip
                onClick={onToggleActive}
                disabled={isSoldOut}
                icon={item.active ? <PowerOff size={16} /> : <Power size={16} />}
                scope={item.active ? "DEZACTIVEAZA" : "ACTIVEAZA"}
                activeColor={item.active ? "hover:border-red-500/60 hover:text-red-400 hover:bg-red-950/40 text-red-400 border-red-900/40" : "hover:border-emerald-500/60 hover:text-emerald-400 hover:bg-emerald-950/40 text-emerald-400 border-emerald-900/40"}
              />
            )}

            <Button
              variant="ghost"
              size="icon"
              disabled={isSoldOut}
              onClick={handleSettingsClick}
              className={`h-10 w-10 rounded-xl border border-slate-900 text-slate-400 transition-colors ${
                isSoldOut
                  ? "opacity-40 cursor-not-allowed text-slate-700 hover:bg-transparent border-slate-950"
                  : "hover:text-white hover:bg-slate-900/60 hover:border-slate-700"
              }`}
              title="Editează detalii"
            >
              <Settings size={14} />
            </Button>

            <IconButtonWithAcarsTooltip
              onClick={onAction}
              icon={<Eye size={16} />}
              scope="VIZUALIZEAZA"
              activeColor="hover:border-orange-500/60 hover:text-orange-400 hover:bg-orange-950/40 text-slate-300 border-slate-800"
            />

            <IconButtonWithAcarsTooltip
              onClick={onDelete}
              icon={<Trash2 size={16} />}
              scope="STERGE"
              activeColor="hover:border-red-600/80 hover:text-red-400 hover:bg-red-950/60 text-slate-500 border-slate-800"
            />
          </div>
        </div>
      </div>

      {(isDraft || isArchived || isUnverified || isSoldOut) && (
        <div className="w-full border-t border-slate-900/60 pt-3">
          {isDraft && (
            <div className="flex items-start gap-2 bg-red-950/30 border border-red-900/50 rounded-xl p-2.5 w-full">
              <FileText size={14} className="text-red-400 shrink-0 mt-0.5" />
              <p className="text-[11px] font-medium text-red-300 normal-case tracking-normal leading-normal">
                Această listare este în modul DRAFT. Nu este publicată și nu este trimisă spre verificare. Apasă butonul de editare pentru a o finaliza și trimite.
              </p>
            </div>
          )}

          {isSoldOut && (
            <div className="flex items-start gap-2 bg-slate-900/60 border border-slate-800 rounded-xl p-2.5 w-full">
              <ShoppingBag size={14} className="text-slate-400 shrink-0 mt-0.5" />
              <p className="text-[11px] font-medium text-slate-400 normal-case tracking-normal leading-normal">
                Stoc epuizat (0 unități). Anunțul este arhivat automat în modul de siguranță.
              </p>
            </div>
          )}

          {isArchived && (
            <div className="flex items-start gap-2 bg-slate-900/40 border border-slate-800/80 rounded-xl p-2.5 w-full">
              <EyeOff size={14} className="text-slate-500 shrink-0 mt-0.5" />
              <p className="text-[11px] font-medium text-slate-400 normal-case tracking-normal leading-normal">
                Listarea este dezactivată temporar. Folosește butonul de activare pentru a o publica din nou pe marketplace.
              </p>
            </div>
          )}

          {isUnverified && (
            <div className="flex items-start gap-2 bg-amber-950/30 border border-amber-900/50 rounded-xl p-2.5 w-full">
              <AlertTriangle size={14} className="text-amber-400 shrink-0 mt-0.5" />
              <p className="text-[11px] font-medium text-amber-300 normal-case tracking-normal leading-normal">
                Activul este în curs de verificare de către echipa noastră de operațiuni. Va fi publicat automat imediat după validare.
              </p>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

const IconButtonWithAcarsTooltip = ({ onClick, disabled, icon, scope, activeColor }: any) => {
  return (
    <div className="relative group/tooltip">
      <Button
        variant="ghost"
        size="icon"
        disabled={disabled}
        onClick={onClick}
        className={`h-11 w-11 rounded-2xl border bg-slate-950/80 transition-all duration-200 active:scale-95 ${activeColor} ${
          disabled ? "opacity-30 cursor-not-allowed hover:bg-transparent border-slate-900 text-slate-700" : ""
        }`}
      >
        {icon}
      </Button>

      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/tooltip:flex flex-col items-center pointer-events-none z-50 animate-in fade-in duration-200">
        <div className="bg-[#030712]/95 border border-emerald-500/40 px-3 py-1.5 rounded-lg shadow-[0_0_15px_rgba(34,197,94,0.15)] backdrop-blur-md whitespace-nowrap flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <span className="font-mono text-[9px] font-black text-[#22c55e] uppercase tracking-wider">
            {scope}
          </span>
        </div>
        <div className="w-2 h-2 bg-[#030712] border-r border-b border-emerald-500/40 rotate-45 -mt-1" />
      </div>
    </div>
  );
};

const EmptyState = ({ message }: { message: string }) => (
  <div className="py-12 px-6 bg-[#0B0F19]/30 rounded-3xl border border-dashed border-slate-900 text-center">
    <p className="text-slate-500 font-mono text-[11px] uppercase tracking-widest font-bold">{message}</p>
  </div>
);

export default SellerDashboard;